import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { YStack, XStack, View, Text, Spinner } from "tamagui";
import { RiArrowLeftSLine, RiCheckLine, RiBankCardLine, RiErrorWarningLine, RiExternalLinkLine } from "react-icons/ri";
import liff from "@line/liff";

import Header from "@components/layout/Header";
import RadiusContentWrapper from "@components/layout/RadiusContentWrapper";
import { colors } from "@styles/colors";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useUser } from "@utils/store/user/useUser";
import { buildXContextHeader } from "@utils/api/headers";
import { setPayReturnTo } from "@utils/payReturnTo";
import { formatGasTitle } from "@utils/func/gasTitle";
import { appToast } from "@components/Toast";
import { AddCardRow, CardBrand, PaymentOptionRow } from "@components/payment/PaymentOptionRow";
import { TapPaySecurityNote } from "@components/payment/TapPayCompliance";
import { isPayLinkMode } from "@utils/payLink";
import { shouldOfferApplePayInLine } from "@utils/applePay";
import ApplePayButton from "@components/payment/ApplePayButton";

import SectionLabel from "../Account/components/ui/SectionLabel";
import { getOrderDetails } from "../OrderList/API/handleOrderAPI";
import {
  handleAPIGetOrderPaymentStatus,
  handleAPIListContextMethods,
  handleAPICreatePaymentIntent,
  handleAPIIssuePayToken,
  type OrderPaymentStatus,
  type ContextPaymentMethod,
  type SnapshotItem,
} from "../Account/API/handlePaymentAPI";
import PayLinkView from "./PayLinkView";

type View_ = "loading" | "error" | "paid" | "not_payable" | "payable" | "success" | "external_pending";

/** 訂單狀態中文標籤（與訂單列表 STS 一致） */
const ORDER_STATUS_LABEL: Record<string, string> = {
  pending: "待確認",
  waiting_for_pickup: "待接單",
  delivering: "配送中",
  completed: "已完成",
  canceled: "已取消",
};
const orderStatusLabel = (s?: string | null) => (s && ORDER_STATUS_LABEL[s]) || s || "-";

/**
 * 訂單付款頁 /pay/:orderId（orderId = platform_order_id UUID）。
 * 兩種進站模式：
 *   - LINE 模式（現況）：既有 LIFF 登入，走這個檔案內的 PayOrderLineMode。
 *   - 一次性付款連結模式（?pt=token，Apple Pay 掃碼／外開 Safari）：無 LINE 登入，
 *     整頁不依賴 cusJwtToken／chooseIdentity，邏輯獨立在 PayLinkView，避免本檔超過 600 行。
 */
export const PayOrder = () => {
  // 凍結掛載當下的模式判斷：PayLinkView 讀完 pt 會 replaceState 把它從網址列移除，
  // 若每次 render 重新呼叫 isPayLinkMode()，那之後任何一次重新渲染都會判斷成「不是 pay-link 模式」，
  // 導致分派器把已經在跑的 PayLinkView 整個換成 PayOrderLineMode（後者又需要 cusJwtToken，直接卡死）。
  const [payLinkMode] = useState(() => isPayLinkMode());
  if (payLinkMode) {
    return <PayLinkView />;
  }
  return <PayOrderLineMode />;
};

/**
 * LINE 模式付款頁本體。入口：送達後 LINE 推播的付款連結。走既有 LINE 登入；後端驗 caller 為訂單 payer，
 * 連結轉傳給他人無法付款。扣款打 POST /payments/intents（同步、防重複扣款、金額後端反查）。
 * Apple Pay（訂單方式為 apple_pay 且在 iPhone LINE 內）另開 Safari 完成，見 openApplePayExternal。
 */
const PayOrderLineMode = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { cusJwtToken } = useCusAuth();
  const { chooseIdentity } = useUser();

  const [view, setView] = useState<View_>("loading");
  const [errMsg, setErrMsg] = useState("");
  const [status, setStatus] = useState<OrderPaymentStatus | null>(null);
  const [items, setItems] = useState<SnapshotItem[]>([]);
  const [displayId, setDisplayId] = useState<string>("");
  const [supplierName, setSupplierName] = useState<string>("");
  const [cards, setCards] = useState<ContextPaymentMethod[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [paying, setPaying] = useState(false);
  const [issuingToken, setIssuingToken] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // 已簽發的一次性付款頁網址：external_pending 畫面「重新開啟付款頁」用，不重新發 token。
  const [externalPayUrl, setExternalPayUrl] = useState<string>("");
  const [successInfo, setSuccessInfo] = useState<{ amount: number; label: string; time: string } | null>(null);

  const cardLabel = (c: ContextPaymentMethod) => `信用卡 •••• ${c.card?.lastfour ?? "----"}`;

  // 掃碼付款連結帶 invite_code：未註冊者會被導去註冊+加入家庭流程，記下這個付款頁，
  // 讓 JoinSuccess 完成後可以導回來繼續付款。要放在最早的 effect，
  // 確保比未註冊被導去註冊的時機更早寫入。
  useEffect(() => {
    if (orderId && new URLSearchParams(window.location.search).has("invite_code")) {
      setPayReturnTo(`/pay/${orderId}`);
    }
  }, [orderId]);

  useEffect(() => {
    if (!cusJwtToken || !orderId) return;
    let alive = true;
    (async () => {
      const [st, detail] = await Promise.all([
        handleAPIGetOrderPaymentStatus(orderId, cusJwtToken),
        getOrderDetails({ orderId, jwtToken: cusJwtToken, xContext: buildXContextHeader(chooseIdentity) }),
      ]);
      if (!alive) return;

      if (st.state !== "success") {
        setErrMsg(typeof st.error === "string" ? st.error : "無法載入付款資訊");
        setView("error");
        return;
      }
      setStatus(st.data);

      if (detail.state === "success") {
        const snap = detail.data?.data?.supplier_order_snapshot_json;
        setItems((snap?.items ?? []) as SnapshotItem[]);
        setDisplayId(snap?.order_list?.order_id ?? "");
        setSupplierName(detail.data?.data?.supplier?.supplier_name ?? chooseIdentity?.primary_supplier?.supplier_name ?? "");
      }

      // 撈可選卡：優先用「訂單付款人 context」（後端回傳），FM 深連結開頁時
      // 前端沒有 chooseIdentity.context_id，改用它才撈得到卡；否則退回當下身分。
      const contextId = st.data.context_id ?? chooseIdentity?.context_id ?? null;
      const ctxMethods = contextId
        ? await handleAPIListContextMethods(contextId, cusJwtToken)
        : null;
      if (!alive) return;
      const ctxItems = ctxMethods && ctxMethods.state === "success" ? ctxMethods.items : [];
      const list = ctxItems.filter((c) => c.method_type === "credit_card" && c.status === "active");
      setCards(list);

      if (st.data.paid) {
        setView("paid");
        return;
      }
      if (!st.data.payable) {
        setView("not_payable");
        return;
      }
      // 預選順位：訂單快照選的卡 → 帳戶預設（含帳戶共用卡）→ 第一張
      const snapCard = st.data.payment_method_id && list.find((c) => c.payment_method_id === st.data.payment_method_id);
      const defId = ctxMethods && ctxMethods.state === "success" ? ctxMethods.defaultId : null;
      const defCard = defId && list.find((c) => c.payment_method_id === defId);
      setSelectedId((snapCard || defCard || list[0])?.payment_method_id ?? "");
      setView("payable");
    })();
    return () => {
      alive = false;
    };
  }, [cusJwtToken, orderId]);

  const pay = async () => {
    if (!orderId || !cusJwtToken || !selectedId || paying) return;
    setPaying(true);
    const r = await handleAPICreatePaymentIntent(orderId, selectedId, cusJwtToken);
    setPaying(false);
    if (r.state === "success") {
      const sel = cards.find((c) => c.payment_method_id === selectedId);
      setSuccessInfo({
        amount: status?.amount ?? r.data?.amount ?? 0,
        label: sel ? cardLabel(sel) : "信用卡",
        time: new Date().toLocaleString("zh-TW", { hour12: false }),
      });
      setView("success");
    } else {
      // 後端錯誤多為英文技術訊息，不可直接顯示給客戶；統一對應中文友善提示。
      const raw = typeof r.error === "string" ? r.error : "";
      let msg = "付款失敗，請稍後再試";
      if (/not authorized|not usable|another context|owner left/i.test(raw)) {
        msg = "此付款方式目前無法使用，請改用其他卡或聯絡瓦斯行";
      } else if (/already.*payment_transaction|active payment/i.test(raw)) {
        msg = "付款處理中，請稍候再試";
      } else if (/not payable|not the payer/i.test(raw)) {
        msg = "此訂單目前無法線上付款";
      }
      appToast.error(msg);
    }
  };

  // 開啟一次性付款頁：LINE 內用 liff.openWindow 外開 Safari；桌面／dev（非 LINE 內）
  // 用 window.open 開新分頁。兩者都可能因瀏覽器封鎖彈窗等原因丟出例外，
  // 失敗僅 toast 提示，畫面仍切到 external_pending（重新開啟付款頁按鈕可再試一次）。
  const openPaymentUrl = (url: string) => {
    try {
      if (liff.isInClient()) {
        liff.openWindow({ url, external: true });
      } else {
        window.open(url, "_blank", "noopener");
      }
    } catch (e) {
      appToast.error("無法開啟付款頁，請點下方按鈕重試");
    }
  };

  // Apple Pay（LINE 內）：不在 LINE 內載 TapPay，按鈕只是入口——簽發一次性 pay_token
  // 後外開付款頁完成付款，回來這頁再手動刷新確認結果。
  const openApplePayExternal = async () => {
    if (!orderId || !cusJwtToken || issuingToken) return;
    setIssuingToken(true);
    const r = await handleAPIIssuePayToken(orderId, cusJwtToken);
    setIssuingToken(false);
    if (r.state === "success") {
      setExternalPayUrl(r.data.url);
      openPaymentUrl(r.data.url);
      setView("external_pending");
    } else {
      appToast.error("無法開啟 Apple Pay 付款，請稍後再試");
    }
  };

  // 「重新開啟付款頁」：沿用已簽發的 pay_token 網址重開，不重新打 issue-pay-token API。
  const reopenPaymentUrl = () => {
    if (!externalPayUrl) return;
    openPaymentUrl(externalPayUrl);
  };

  const refreshExternalPaymentStatus = async () => {
    if (!orderId || !cusJwtToken || refreshing) return;
    setRefreshing(true);
    const r = await handleAPIGetOrderPaymentStatus(orderId, cusJwtToken);
    setRefreshing(false);
    if (r.state !== "success") {
      appToast.error("無法取得付款狀態，請稍後再試");
      return;
    }
    setStatus(r.data);
    if (r.data.paid) {
      setView("paid");
      return;
    }
    appToast.error("尚未收到付款");
  };

  // 綁卡：走既有 TapPay 表單，綁完 returnTo 回本頁重新載入
  // returnContext：3DS challenge 完成後（§6），落地頁依此導回本頁繼續付款
  const goBindCard = () => {
    navigate("/account/paymentMethod", {
      state: {
        bindScope: "personal",
        returnTo: `/pay/${orderId}`,
        returnContext: { type: "pay_order", order_id: orderId ?? "", return_path: `/pay/${orderId}` },
      },
    });
  };

  const back = () => navigate(`/orders/${orderId}`);

  const headerNode = (
    <Header>
      <XStack ai="center" onPress={back} cursor="pointer" mb="$3">
        <View mr="$2">
          <RiArrowLeftSLine size={20} color={colors.light} />
        </View>
        <Text color={colors.light} fontWeight="bold" fontSize={16}>
          返回
        </Text>
      </XStack>
      <Text color={colors.light} fontSize={22} fontWeight="800" mb="$1.5">
        訂單付款
      </Text>
      <Text color="rgba(255,255,255,0.85)" fontSize={12}>
        {supplierName}
        {displayId ? ` · 訂單 ${displayId}` : ""}
      </Text>
    </Header>
  );

  if (view === "loading") {
    return (
      <YStack height="100dvh" width="100vw" background={colors.bg}>
        {headerNode}
        <YStack f={1} ai="center" jc="center">
          <Spinner size="large" color={colors.primary} />
        </YStack>
      </YStack>
    );
  }

  if (view === "error") {
    return (
      <YStack height="100dvh" width="100vw" background={colors.bg}>
        {headerNode}
        <YStack f={1} ai="center" jc="center" gap="$2" px="$6">
          <RiErrorWarningLine size={40} color={colors.hint} />
          <Text fontSize={14} color={colors.sub} ta="center">
            {errMsg}
          </Text>
          <Text fontSize={12} color={colors.hint} ta="center">
            請確認以正確的帳號開啟此付款連結
          </Text>
        </YStack>
      </YStack>
    );
  }

  if (view === "external_pending") {
    return (
      <YStack height="100dvh" width="100vw" background={colors.bg}>
        {headerNode}
        <YStack f={1} ai="center" jc="center" gap="$2" px="$6">
          <RiExternalLinkLine size={40} color={colors.primary} />
          <Text fontSize={15} fontWeight="700" color={colors.text} ta="center">
            已在 Safari 開啟付款頁
          </Text>
          <Text fontSize={12} color={colors.hint} ta="center">
            完成付款後回到這裡
          </Text>
          <YStack width="100%" gap="$2" mt="$4">
            <YStack
              br={12}
              py="$3"
              ai="center"
              bw={1}
              bc={colors.gray100}
              bg={colors.light}
              cursor="pointer"
              pressStyle={{ opacity: 0.8 }}
              onPress={reopenPaymentUrl}>
              <Text fontSize={14} fontWeight="700" color={colors.text}>
                重新開啟付款頁
              </Text>
            </YStack>
            <YStack
              br={12}
              py="$3.5"
              ai="center"
              className="primary-bg-gradient"
              opacity={refreshing ? 0.6 : 1}
              cursor="pointer"
              pressStyle={{ opacity: 0.85 }}
              onPress={refreshExternalPaymentStatus}>
              {refreshing ? (
                <Spinner size="small" color="#fff" />
              ) : (
                <Text fontSize={15} fontWeight="800" color={colors.light}>
                  重新整理付款狀態
                </Text>
              )}
            </YStack>
            <YStack
              br={12}
              py="$3"
              ai="center"
              bw={1}
              bc={colors.gray100}
              bg={colors.light}
              cursor="pointer"
              pressStyle={{ opacity: 0.8 }}
              onPress={() => setView("payable")}>
              <Text fontSize={14} fontWeight="700" color={colors.text}>
                改用信用卡付款
              </Text>
            </YStack>
          </YStack>
        </YStack>
      </YStack>
    );
  }

  if (view === "success" || view === "paid") {
    const tx = status?.transaction;
    const amount = view === "success" ? successInfo?.amount ?? 0 : tx?.amount ?? status?.amount ?? 0;
    const label = view === "success" ? successInfo?.label ?? "信用卡" : tx?.lastfour ? `信用卡 •••• ${tx.lastfour}` : status?.label ?? "信用卡";
    const timeText =
      view === "success" ? successInfo?.time ?? "" : tx?.paid_at ? new Date(tx.paid_at).toLocaleString("zh-TW", { hour12: false }) : "";
    return (
      <YStack height="100dvh" width="100vw" background={colors.bg}>
        {headerNode}
        <RadiusContentWrapper>
          <YStack ai="center" gap="$3" pt="$6">
            <View w={72} h={72} br={36} bg="#eafaf0" ai="center" jc="center">
              <RiCheckLine size={36} color={colors.green} />
            </View>
            <Text fontSize={17} fontWeight="800" color={colors.text}>
              {view === "success" ? "付款成功" : "這筆訂單已完成付款"}
            </Text>
            <Text fontSize={12} color={colors.sub} ta="center">
              {view === "success" ? "已通知瓦斯行，訂單狀態同步更新" : timeText ? `${timeText} 已完成付款` : "已完成付款"}
            </Text>
            <YStack bg={colors.light} bw={1} bc={colors.borderLight} br={12} px="$4" py="$3" width="100%" gap="$1.5">
              <XStack jc="space-between">
                <Text fontSize={13} color={colors.sub}>
                  付款金額
                </Text>
                <Text fontSize={13} fontWeight="700" color={colors.text} style={{ fontVariantNumeric: "tabular-nums" }}>
                  NT$ {amount.toLocaleString()}
                </Text>
              </XStack>
              <XStack jc="space-between">
                <Text fontSize={13} color={colors.sub}>
                  付款方式
                </Text>
                <Text fontSize={13} fontWeight="700" color={colors.text}>
                  {label}
                </Text>
              </XStack>
              {view === "success" && successInfo?.time ? (
                <XStack jc="space-between">
                  <Text fontSize={13} color={colors.sub}>
                    時間
                  </Text>
                  <Text fontSize={13} fontWeight="700" color={colors.text}>
                    {successInfo.time}
                  </Text>
                </XStack>
              ) : null}
            </YStack>
            <YStack
              width="100%"
              br={12}
              py="$3"
              ai="center"
              bw={1}
              bc={colors.gray100}
              bg={colors.light}
              cursor="pointer"
              pressStyle={{ opacity: 0.8 }}
              onPress={back}>
              <Text fontSize={14} fontWeight="700" color={colors.text}>
                查看訂單詳細
              </Text>
            </YStack>
          </YStack>
        </RadiusContentWrapper>
      </YStack>
    );
  }

  if (view === "not_payable") {
    const st = status?.order_status;
    // 線上付款開放時機：配送中之後（2026-08-13 拍板）
    const hint =
      status?.payment_status === "cash_fallback"
        ? "配送員已改為現金收款，請以現金支付給配送員。"
        : st === "pending" || st === "waiting_for_pickup"
          ? "瓦斯配送中才能線上付款，請稍候再回來這頁。"
          : st === "canceled"
            ? "訂單已取消，無需付款。"
            : st === "completed"
              ? "訂單已完成結案；若對款項有疑問請聯絡瓦斯行。"
              : "若已現場付款，無需再付款；有疑問請聯絡瓦斯行。";
    return (
      <YStack height="100dvh" width="100vw" background={colors.bg}>
        {headerNode}
        <YStack f={1} ai="center" jc="center" gap="$2" px="$6">
          <RiErrorWarningLine size={40} color={colors.hint} />
          <Text fontSize={15} fontWeight="700" color={colors.text}>
            這筆訂單尚未開放線上付款
          </Text>
          <Text fontSize={12} color={colors.hint} ta="center">
            訂單狀態：{orderStatusLabel(st)}。{hint}
          </Text>
        </YStack>
      </YStack>
    );
  }

  // view === "payable"
  const myCards = cards.filter((c) => c.is_owned_by_caller);
  const sharedCards = cards.filter((c) => !c.is_owned_by_caller);
  // 不要求在 LINE App 內：外部 Safari 開 LIFF 連結登入後的 LINE 模式頁也要顯示按鈕；
  // 按下後 openPaymentUrl 依 liff.isInClient() 分流開啟方式。
  // devOverride：開發環境不受裝置限制（略過 os 檢查），方便桌面 dev 測試；
  // 與 Kiosk 結帳頁 CheckoutGuide 的 Apple Pay 判斷一致。
  const showApplePayInLine = shouldOfferApplePayInLine({
    os: liff.getOS() ?? null,
    paymentMethod: status?.payment_method ?? null,
    payable: status?.payable ?? false,
    devOverride: process.env.NODE_ENV === "development",
  });
  return (
    <YStack height="100dvh" width="100vw" background={colors.bg}>
      {headerNode}
      <RadiusContentWrapper space="$4">
        <YStack>
          <SectionLabel title="訂單摘要" />
          <YStack bg={colors.light} bw={1} bc={colors.borderLight} br={12} px="$4" py="$3" gap="$1">
            {items.map((it, idx) => {
              const title = it.kind === "gas" ? formatGasTitle(it.gas_type, it.gas_kilogram ?? "") : it.commodity_name ?? "商品";
              const qty = it.kind === "gas" ? it.numbers_of_cylinder : it.numbers_of_commodity;
              return (
                <Text key={idx} fontSize={13} color={colors.sub}>
                  {title} × {qty ?? 1}
                </Text>
              );
            })}
            <XStack jc="space-between" ai="baseline" mt="$2" pt="$2" btw={1} btc={colors.borderLight}>
              <Text fontSize={12} color={colors.sub}>
                應付金額
              </Text>
              <Text fontSize={22} fontWeight="800" color={colors.primary} style={{ fontVariantNumeric: "tabular-nums" }}>
                NT$ {(status?.amount ?? 0).toLocaleString()}
              </Text>
            </XStack>
          </YStack>
        </YStack>

        <YStack>
          <SectionLabel title="選擇付款卡" />
          {cards.length === 0 ? (
            <YStack bg={colors.light} bw={1} bc={colors.borderLight} br={12} px="$4" py="$5" ai="center" gap="$1">
              <RiBankCardLine size={26} color={colors.hint} />
              <Text fontSize={13.5} fontWeight="700" color={colors.text}>
                還沒有可用的信用卡
              </Text>
              <Text fontSize={11.5} color={colors.sub}>
                先綁定一張卡，之後付款一鍵完成
              </Text>
            </YStack>
          ) : (
            <YStack gap={10}>
              {myCards.map((c) => (
                <PaymentOptionRow
                  key={c.payment_method_id}
                  title={cardLabel(c)}
                  sub={c.card?.issuer || c.nickname || "信用卡"}
                  leading={<CardBrand bincode={c.card?.bincode} />}
                  selected={selectedId === c.payment_method_id}
                  onPress={() => setSelectedId(c.payment_method_id)}
                />
              ))}
              {sharedCards.length > 0 ? (
                <YStack mt="$1" gap={10}>
                  <SectionLabel title="帳戶共用卡" />
                  {sharedCards.map((c) => (
                    <PaymentOptionRow
                      key={c.payment_method_id}
                      title={cardLabel(c)}
                      sub={c.added_by_name ? `${c.added_by_name} 提供` : "帳戶共用卡"}
                      leading={<CardBrand bincode={c.card?.bincode} />}
                      selected={selectedId === c.payment_method_id}
                      onPress={() => setSelectedId(c.payment_method_id)}
                    />
                  ))}
                </YStack>
              ) : null}
              <AddCardRow onPress={goBindCard} />
            </YStack>
          )}
          {/* TapPay 金流安全揭露（審核要求：付款頁需說明金流商與加密／PCI 認證） */}
          <YStack mt="$2.5">
            <TapPaySecurityNote compact />
          </YStack>
        </YStack>

        <YStack gap="$2" pb="$6">
          {cards.length === 0 ? (
            <YStack
              br={12}
              py="$3.5"
              ai="center"
              className="primary-bg-gradient"
              cursor="pointer"
              pressStyle={{ opacity: 0.85 }}
              onPress={goBindCard}>
              <Text fontSize={15} fontWeight="800" color={colors.light}>
                綁定信用卡並付款
              </Text>
            </YStack>
          ) : (
            <YStack
              br={12}
              py="$3.5"
              ai="center"
              className="primary-bg-gradient"
              opacity={!selectedId || paying ? 0.6 : 1}
              cursor="pointer"
              pressStyle={{ opacity: 0.8 }}
              onPress={pay}>
              {paying ? (
                <Spinner size="small" color="#fff" />
              ) : (
                <Text fontSize={15} fontWeight="800" color={colors.light}>
                  確認付款 NT$ {(status?.amount ?? 0).toLocaleString()}
                </Text>
              )}
            </YStack>
          )}
          {showApplePayInLine ? (
            <>
              <Text fontSize={12} color={colors.hint} ta="center" my="$1">
                — 或 —
              </Text>
              <ApplePayButton state="ready" note="將立即付款 · 會開啟 Safari 完成" onPress={openApplePayExternal} />
            </>
          ) : null}
        </YStack>
      </RadiusContentWrapper>
    </YStack>
  );
};

export default PayOrder;
